import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { User, LogOut, Settings, Globe, FileText, Mail, Home, MessageSquare } from 'lucide-react';
import SiteLogo from './SiteLogo';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar: React.FC = () => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  const navigation = useMemo(
    () => [
      { name: t('nav.home'), href: '/', icon: Home },
      { name: t('nav.about'), href: '/sobre', icon: Globe },
      { name: t('nav.immigration'), href: '/imigracao', icon: FileText },
      { name: t('nav.blog'), href: '/blog', icon: FileText },
      { name: t('nav.testimonials'), href: '/depoimentos', icon: MessageSquare },
      { name: t('nav.contact'), href: '/contato', icon: Mail }
    ],
    [t]
  );

  const isActive = (path: string) => location.pathname === path;

  const navLinkClass = (active: boolean) =>
    `flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
      active
        ? 'bg-primary-100 text-primary-700'
        : 'text-gray-600 hover:text-primary-600 hover:bg-gray-50'
    }`;

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="container-custom px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4 lg:gap-6 py-3 md:py-3.5 min-h-[4rem]">
          <Link to="/" className="shrink-0">
            <SiteLogo size="sm" nameClassName="font-bold text-gray-900" />
          </Link>

          <div className="hidden lg:flex flex-1 items-center justify-center gap-1 min-w-0">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link key={item.href} to={item.href} className={navLinkClass(isActive(item.href))}>
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </div>

          <div className="hidden lg:flex items-center gap-4 shrink-0 pl-5 ml-1 border-l border-gray-200">
            <LanguageSwitcher />
            {isAuthenticated ? (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors duration-200 max-w-[11rem]"
                >
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-primary-600" />
                  </div>
                  <span className="text-sm text-gray-700 truncate">{user?.name}</span>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Settings className="w-4 h-4" />
                        <span>{t('nav.admin')}</span>
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        logout();
                        setIsUserMenuOpen(false);
                      }}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>{t('nav.logout')}</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="px-4 py-2 text-sm font-semibold bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 whitespace-nowrap"
              >
                {t('nav.login')}
              </Link>
            )}
          </div>

          <div className="lg:hidden flex items-center gap-3 ml-auto">
            <LanguageSwitcher compact />
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
